import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Save, CheckCircle, PackageCheck, Trash2, Filter, X, ExternalLink, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { isRecent } from '@/lib/uxHelpers';
import RefLink from '@/components/RefLink';
import StatusBadge from '@/components/StatusBadge';
import exportCsv from '@/lib/exportCsv';
import { useStats } from '@/contexts/StatsContext';
import { markListSeen, ENTITY_TYPES } from '@/lib/readState';
import { toastApiError } from '@/lib/errors';
import RegistryCombobox from '@/components/RegistryCombobox';
import CompanyFormDialog from '@/components/CompanyFormDialog';
import { Checkbox } from '@/components/ui/checkbox';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';
import InstrumentFormDialog from '@/components/InstrumentFormDialog';

const EMPTY_DRAFT = {
  customer: { company: '', contactPerson: '', email: '', phone: '' },
  instruments: [],
  transport: '',
  notes: '',
};

// Telefon není povinný — chodí od zákazníka (vč. objednávek z portálu), lab ho sám nezná.
const REQUIRED_CUSTOMER_FIELDS = ['company', 'contactPerson', 'email'];

export default function OrderRegistryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterInstrumentName = searchParams.get('instrumentName');
  const filterInstrumentType = searchParams.get('instrumentType');
  const [orders, setOrders] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [relatedIntakes, setRelatedIntakes] = useState([]);
  const [relatedCalibrations, setRelatedCalibrations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState(filterInstrumentName ? 'all' : 'new');
  const [newInstrumentId, setNewInstrumentId] = useState('');
  const [newMarking1, setNewMarking1] = useState('');
  const [newMarking2, setNewMarking2] = useState('');
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDialogName, setCompanyDialogName] = useState('');
  const [instrumentDialogOpen, setInstrumentDialogOpen] = useState(false);
  const { refetch: refetchStats } = useStats();

  const clearFilter = () => {
    setSearchParams({});
  };

  const load = () => {
    api.get('/orders').then(r => setOrders(r.data)).catch(() => {});
    api.get('/instruments').then(r => setInstruments(r.data)).catch(() => {});
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/intakes').then(r => setRelatedIntakes(r.data)).catch(() => {});
    api.get('/calibrations').then(r => setRelatedCalibrations(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Sidebar badge = confirmed objednávky čekající na příjem. Po zobrazení listu je mark-seen.
  useEffect(() => {
    if (orders.length === 0) return;
    const confirmedIds = orders.filter(o => o.status === 'confirmed').map(o => o.id).filter(Boolean);
    if (confirmedIds.length === 0) return;
    markListSeen(ENTITY_TYPES.ORDER, confirmedIds).then(() => refetchStats());
  }, [orders, refetchStats]);

  // Auto-select přes URL param `?edit=<id>` (cesta z OrderDetailPage „Upravit").
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam || orders.length === 0) return;
    const target = orders.find(o => o.id === editParam);
    if (target) {
      selectOrder(target);
      setTab('all');
    }
    // Po použití vyčistit URL param, aby se nespustilo znovu při další akci.
    setSearchParams({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, orders]);

  // UX vrstva A: pro vybranou objednávku najít navázaný intake (1:1) a kalibrace (N).
  const isExistingOrder = selected && selected !== '__new__';
  const intakeForOrder = isExistingOrder
    ? relatedIntakes.find(i => i.orderId === selected)
    : null;
  const calibrationsForOrder = isExistingOrder
    ? relatedCalibrations.filter(c => c.orderId === selected)
    : [];

  const companyName = (id) => companies.find(c => c.id === id)?.name || '';
  const resolveManufacturer = (v) => companyName(v) || v || '';

  // Firma na objednávce z číselníku (filtr na zákazníky,
  // legacy fallback — pokud žádná firma nemá flag isCustomer, ukážeme všechny).
  const customerCompanies = (() => {
    const hasCustomerFlag = companies.some(c => c.isCustomer);
    let list = hasCustomerFlag ? companies.filter(c => c.isCustomer) : companies;
    // Aktuálně vybranou firmu vždy nech ve výběru (i kdyby neměla flag isCustomer),
    // ať ji jde znovu vybrat a vidět zaškrtnutí (ne jen displayLabel fallback).
    const selId = form.customer?.companyId;
    if (selId && !list.some(c => c.id === selId)) {
      const sel = companies.find(c => c.id === selId);
      if (sel) list = [sel, ...list];
    }
    return list;
  })();
  const selectOrderCompany = (company) => {
    setForm({
      ...form,
      customer: {
        ...form.customer,
        company: company?.name || '',
        companyId: company?.id || null,
      },
    });
  };
  const handleCompanyCreated = (company) => {
    setCompanies(prev => [...prev, company]);
    selectOrderCompany(company);
  };
  // Nově vytvořený přístroj rovnou předvybereme ve výběru,
  // ať ho laborant jen potvrdí tlačítkem „Přidat".
  const handleInstrumentCreated = (inst) => {
    setInstruments(prev => [...prev, inst]);
    setNewInstrumentId(inst.id);
  };

  const selectOrder = (o) => {
    setSelected(o.id);
    setForm({ ...o, instruments: o.instruments || [] });
    setNewInstrumentId('');
    setNewMarking1('');
    setNewMarking2('');
  };

  // ADR-006 + #4: "+" otevře jen draft, POST se odešle teprve při Save.
  const isDraft = selected === '__new__';
  const createDraft = () => {
    setSelected('__new__');
    setForm({ ...EMPTY_DRAFT, instruments: [] });
    setNewInstrumentId('');
    setNewMarking1('');
    setNewMarking2('');
  };
  const cancelDraft = () => {
    setSelected(null);
    setForm({});
  };

  // #15: přístroje do objednávky pouze výběrem z evidence (FK).
  const addOrderInstrument = () => {
    if (!newInstrumentId) {
      toast.error(t('orders.error-pick-instrument'));
      return;
    }
    const ref = instruments.find(i => i.id === newInstrumentId);
    if (!ref) return;
    const item = {
      instrumentId: newInstrumentId,
      name: ref.name || '',
      type: ref.type || '',
      manufacturer: resolveManufacturer(ref.manufacturer),
      serialNumber: ref.serialNumber || '',
      marking1: newMarking1 || '',
      marking2: newMarking2 || '',
    };
    const list = [...(form.instruments || []), item];
    setForm({ ...form, instruments: list, instrumentCount: list.length });
    setNewInstrumentId('');
    setNewMarking1('');
    setNewMarking2('');
  };

  const removeOrderInstrument = (idx) => {
    const instruments = (form.instruments || []).filter((_, i) => i !== idx);
    setForm({ ...form, instruments, instrumentCount: instruments.length });
  };

  const missingCustomerLabels = REQUIRED_CUSTOMER_FIELDS
    .filter(field => !String(form.customer?.[field] || '').trim())
    .map(field => {
      const labelMap = {
        company: t('orders.label-company'),
        contactPerson: t('orders.label-contact-person'),
        email: t('orders.label-email'),
        phone: t('orders.label-phone'),
      };
      return labelMap[field] || field;
    });
  const emailValid = (() => {
    const e = String(form.customer?.email || '').trim();
    if (!e) return false;
    return e.includes('@') && e.split('@')[1]?.includes('.');
  })();
  const validationErrors = [];
  if (missingCustomerLabels.length > 0) validationErrors.push(t('orders.validation-missing', { fields: missingCustomerLabels.join(', ') }));
  if (form.customer?.email && !emailValid) validationErrors.push(t('orders.validation-email-invalid'));
  if (!(form.instruments || []).length) validationErrors.push(t('orders.validation-no-instruments'));
  const canSave = validationErrors.length === 0;

  const save = async () => {
    if (!selected) return;
    if (!canSave) {
      toast.error(validationErrors.join('; '));
      return;
    }
    try {
      if (isDraft) {
        const res = await api.post('/orders', form);
        load();
        selectOrder(res.data);
        toast.success(t('orders.toast-created'));
      } else {
        await api.put(`/orders/${selected}`, form);
        load();
        toast.success(t('orders.toast-saved'));
      }
    } catch (err) {
      toastApiError(err);
    }
  };

  const confirm = async () => {
    if (!selected) return;
    try {
      await api.put(`/orders/${selected}/confirm`);
      load();
      refetchStats();
      toast.success(t('orders.toast-confirmed'));
    } catch { toast.error(t('orders.error-default')); }
  };

  const receive = async () => {
    if (!selected) return;
    try {
      await api.put(`/orders/${selected}/receive`);
      load();
      refetchStats();
      toast.success(t('orders.toast-received'));
    } catch { toast.error(t('orders.error-default')); }
  };

  // 1-klik: potvrdit (je-li nová) → vytvořit příjem → odeslat ke kalibraci.
  // Zkrátí 3 kroky napříč obrazovkami do jedné akce.
  const receiveToCalibration = async () => {
    if (!selected) return;
    const instruments = (form.instruments || [])
      .map(i => ({ instrumentId: i.instrumentId }))
      .filter(x => x.instrumentId);
    if (!instruments.length) { toast.error('Objednávka nemá žádný přístroj z evidence.'); return; }
    try {
      if (form.status === 'new') await api.put(`/orders/${selected}/confirm`);
      let intakeId;
      try {
        const r = await api.post('/intakes', { orderId: selected, instruments, generateProtocol: true });
        intakeId = r.data.id;
      } catch (e) {
        // Jeden order = jeden intake — pokud už existuje, dohledej a použij.
        const ints = await api.get('/intakes').then(r => r.data).catch(() => []);
        const ex = (ints || []).find(x => x.orderId === selected);
        if (!ex) throw e;
        intakeId = ex.id;
      }
      // Pre-check: přístroj bez sond/metodiky kalibraci nevytvoří. Když by
      // nevznikla žádná, zastav (jinak by uživatel viděl „úspěch" a prázdné kalibrace).
      const pv = (await api.get(`/intakes/${intakeId}/calibration-preview`)).data;
      const skipped = [
        ...(pv.instrumentsWithoutProbes || []),
        ...(pv.unresolvedInstruments || []),
        ...(pv.instrumentsNeedingMethodology || []),
      ];
      if ((pv.willCreate || 0) === 0) {
        toast.error(
          `Přijato, ale ke kalibraci nelze odeslat — žádný přístroj nemá sondu s metodikou (${skipped.join(', ') || 'bez sond'}). Doplň sondy v evidenci přístrojů.`,
          { duration: 9000 },
        );
        load();
        refetchStats();
        return;
      }
      if (skipped.length > 0 && !window.confirm(
        `Tyto přístroje budou přeskočeny (nemají sondu / metodiku): ${skipped.join(', ')}.\n\nOdeslat ke kalibraci zbytek (${pv.willCreate})?`,
      )) {
        load();
        return;
      }
      const res = await api.put(`/intakes/${intakeId}/send-to-calibration`);
      load();
      refetchStats();
      const created = res.data?.calibrationsCreated || 0;
      toast.success(`Přijato a odesláno ke kalibraci — založeno ${created} kalibrací.`);
    } catch (err) {
      toastApiError(err);
    }
  };

  const remove = async () => {
    if (!selected || isDraft) return;
    if (!window.confirm(t('orders.confirm-delete'))) return;
    await api.delete(`/orders/${selected}`);
    setSelected(null);
    load();
  };

  const filtered = orders.filter(o => {
    // Status tab filter
    if (tab === 'new' && o.status !== 'new') return false;
    if (tab === 'confirmed' && o.status !== 'confirmed') return false;
    if (tab === 'received' && o.status !== 'received') return false;

    // Instrument filter z URL (proklik z InstrumentRegistryPage „Celkem zakázek")
    if (filterInstrumentName) {
      const hasMatch = (o.instruments || []).some(i =>
        i.name === filterInstrumentName &&
        (!filterInstrumentType || i.type === filterInstrumentType)
      );
      if (!hasMatch) return false;
    }

    return true;
  });

  // #3: typová sanitizace telefonu — povolené jen číslice, +, -, (, ), mezera.
  const sanitizePhone = (v) => v.replace(/[^\d+\-\s()]/g, '');

  const updateCustomer = (field, value) => {
    let v = value;
    if (field === 'phone') v = sanitizePhone(value);
    setForm({ ...form, customer: { ...form.customer, [field]: v } });
  };

  const sel = useBulkSelect(filtered.map(o => o.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/orders/${id}`));
    if (selected && ids.includes(selected)) { setSelected(null); }
    sel.clear();
    load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo (možná mají příjem/kalibraci)`);
    else toast.success(`Smazáno ${ok}`);
  };

  return (
    <div data-testid="order-registry-page" className="flex flex-col gap-4 h-[calc(100vh-7rem)]">
      {filterInstrumentName && (
        <div
          data-testid="order-filter-banner"
          className="flex items-center gap-3 px-3 py-2 rounded text-sm"
          style={{
            background: 'rgba(var(--ct-accent-rgb), 0.08)',
            border: '1px solid var(--ct-accent)',
          }}
        >
          <Filter className="w-4 h-4" style={{ color: 'var(--ct-accent)' }} />
          <span style={{ color: 'var(--ct-text)' }}>
            {t('orders.filter-banner-prefix')}{' '}
            <strong style={{ color: 'var(--ct-accent)' }}>
              {filterInstrumentName}{filterInstrumentType ? ` · ${filterInstrumentType}` : ''}
            </strong>
          </span>
          <span className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
            {t('orders.filter-banner-help')}
          </span>
          <button
            data-testid="clear-order-filter"
            onClick={clearFilter}
            className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded hover:opacity-80"
            style={{ color: 'var(--ct-text-secondary)' }}
            title={t('orders.filter-clear-title')}
          >
            <X className="w-3 h-3" /> {t('orders.filter-clear')}
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList style={{ background: 'var(--ct-surface)', borderColor: 'var(--ct-border)' }}>
            <TabsTrigger value="all" data-testid="tab-all-orders">
              {t('orders.tab-all')} ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="new" data-testid="tab-new-orders">
              {t('orders.tab-new')} ({orders.filter(o => o.status === 'new').length})
            </TabsTrigger>
            <TabsTrigger value="confirmed" data-testid="tab-confirmed-orders">
              {t('orders.tab-confirmed')} ({orders.filter(o => o.status === 'confirmed').length})
            </TabsTrigger>
            <TabsTrigger value="received" data-testid="tab-received-orders">
              {t('orders.tab-received')} ({orders.filter(o => o.status === 'received').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <button
          type="button"
          onClick={() => exportCsv({
            filename: `${t('orders.csv-filename-prefix')}-${new Date().toISOString().slice(0, 10)}.csv`,
            rows: filtered,
            columns: [
              { key: 'orderNumber', label: t('orders.csv-col-number') },
              { key: 'status', label: t('orders.csv-col-status'), format: v => v === 'new' ? t('orders.status-new') : v === 'confirmed' ? t('orders.status-confirmed') : v === 'received' ? t('orders.status-received') : v },
              { key: (o) => o.customer?.company, label: t('orders.csv-col-company') },
              { key: (o) => o.customer?.contactPerson, label: t('orders.csv-col-contact') },
              { key: (o) => o.customer?.email, label: t('orders.csv-col-email') },
              { key: (o) => o.customer?.phone, label: t('orders.csv-col-phone') },
              { key: 'instrumentCount', label: t('orders.csv-col-instrument-count') },
              { key: 'createdAt', label: t('orders.csv-col-created'), format: v => v ? new Date(v).toLocaleDateString(t('common.date-locale')) : '' },
              { key: 'confirmedAt', label: t('orders.csv-col-confirmed'), format: v => v ? new Date(v).toLocaleDateString(t('common.date-locale')) : '' },
              { key: 'receivedAt', label: t('orders.csv-col-received'), format: v => v ? new Date(v).toLocaleDateString(t('common.date-locale')) : '' },
            ],
          })}
          disabled={filtered.length === 0}
          className="h-9 flex items-center gap-1.5 px-3 text-sm rounded border transition-colors disabled:opacity-40"
          style={{ background: 'var(--ct-surface)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
          title={t('orders.csv-export-title', { count: filtered.length })}
        >
          <FileSpreadsheet className="w-4 h-4" />
          {t('orders.csv-export-btn')}
        </button>
      </div>

      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Left: List */}
        <div className="ct-panel w-[450px] flex-shrink-0 flex flex-col">
          <div className="ct-panel-header flex items-center justify-between">
            <span>{t('orders.list-title')}</span>
            <button data-testid="add-order-btn" onClick={createDraft} className="p-1 rounded" style={{ color: 'var(--ct-accent)' }}>
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {filtered.length > 0 && (
            <div className="p-2 space-y-2">
              <label className="flex items-center gap-2 px-1 text-xs cursor-pointer" style={{ color: 'var(--ct-text-secondary)' }}>
                <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} aria-label="Vybrat vše" />
                Vybrat vše
              </label>
              <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="objednávek" />
            </div>
          )}
          <ScrollArea className="flex-1">
            {filtered.map(o => (
              <div
                key={o.id}
                className={`ct-table-row flex items-center gap-2 px-3 py-2 text-sm ${selected === o.id ? 'active' : ''} ${isRecent(o.createdAt) ? 'ct-row-new' : ''}`}
              >
                <Checkbox
                  checked={sel.isSelected(o.id)}
                  onCheckedChange={() => sel.toggle(o.id)}
                  aria-label={`Vybrat ${o.orderNumber}`}
                />
                <button
                  onClick={() => selectOrder(o)}
                  className="flex-1 text-left"
                  title={isRecent(o.createdAt) ? t('orders.list-row-recent-title') : undefined}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-mono text-xs" style={{ color: 'var(--ct-accent)' }}>{o.orderNumber}</div>
                      <div>{o.customer?.company || t('orders.list-no-customer')}</div>
                    </div>
                    <div className="text-right">
                      <StatusBadge status={o.status} />
                      <div className="text-xs mt-1 font-mono" style={{ color: 'var(--ct-text-secondary)' }}>
                        {o.createdAt ? new Date(o.createdAt).toLocaleDateString(t('common.date-locale')) : ''}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            ))}
            {filtered.length === 0 && <div className="p-4 text-center text-sm" style={{ color: 'var(--ct-text-disabled)' }}>{t('orders.list-empty')}</div>}
          </ScrollArea>
        </div>

        {/* Right: split-view editor — primární UX pro view/edit. „Otevřít detail" v headeru naviguje na /orders/:id. */}
        {selected ? (
          <div className="flex-1 overflow-auto space-y-4">
            <div className="ct-panel">
              <div className="ct-panel-header flex items-center justify-between">
                <span>{isDraft ? t('orders.draft-header') : t('orders.detail-header', { number: form.orderNumber })}</span>
                {!isDraft && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => navigate(`/orders/${selected}`)}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-opacity hover:opacity-80"
                      style={{ color: 'var(--ct-accent)' }}
                      title={t('orders.btn-open-detail-title')}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {t('orders.btn-open-detail')}
                    </button>
                    <button onClick={remove} className="p-1 rounded" style={{ color: 'var(--ct-error)' }} title={t('orders.btn-delete-title')}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="p-4 space-y-4">
                <h3 className="ct-eyebrow">{t('orders.section-customer')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="ct-form-field">
                    <label className="ct-form-label">{t('orders.label-company')}</label>
                    <RegistryCombobox
                      testid="order-company-combobox"
                      value={form.customer?.companyId || ''}
                      displayLabel={form.customer?.company || ''}
                      items={customerCompanies}
                      getKey={(c) => c.id}
                      getLabel={(c) => c.name}
                      getSublabel={(c) => [c.city, c.ico].filter(Boolean).join(' · ')}
                      onSelect={selectOrderCompany}
                      onCreateNew={(text) => { setCompanyDialogName(text); setCompanyDialogOpen(true); }}
                      placeholder={t('orders.company-picker-placeholder')}
                      searchPlaceholder={t('common.search')}
                      emptyText={t('orders.company-picker-empty')}
                      createLabel={t('orders.company-create-new')}
                    />
                  </div>
                  <div className="ct-form-field">
                    <label className="ct-form-label">{t('orders.label-contact-person')}</label>
                    <Input value={form.customer?.contactPerson || ''} onChange={e => updateCustomer('contactPerson', e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div className="ct-form-field">
                    <label className="ct-form-label">{t('orders.label-email')}</label>
                    <Input
                      value={form.customer?.email || ''}
                      onChange={e => updateCustomer('email', e.target.value)}
                      type="email"
                      className="h-9 text-sm"
                      style={form.customer?.email && !emailValid ? { borderColor: 'var(--ct-error)' } : undefined}
                    />
                    {form.customer?.email && !emailValid && (
                      <p className="text-xs mt-1" style={{ color: 'var(--ct-error)' }}>
                        {t('orders.email-invalid')}
                      </p>
                    )}
                  </div>
                  <div className="ct-form-field">
                    <label className="ct-form-label">{t('orders.label-phone')}</label>
                    <Input
                      value={form.customer?.phone || ''}
                      onChange={e => updateCustomer('phone', e.target.value)}
                      inputMode="tel"
                      placeholder={t('orders.label-phone-placeholder')}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="ct-form-field">
                    <label className="ct-form-label">{t('orders.label-instrument-count-auto')}</label>
                    <div
                      className="h-9 flex items-center px-3 rounded font-mono text-sm"
                      style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text-secondary)' }}
                    >
                      {(form.instruments?.length ?? 0)}
                    </div>
                  </div>
                  <div className="ct-form-field">
                    <label className="ct-form-label">{t('orders.label-transport')}</label>
                    <Input value={form.transport || ''} onChange={e => setForm({ ...form, transport: e.target.value })} className="h-9 text-sm" />
                  </div>
                </div>

                {/* Instruments in order */}
                <div>
                  <h3 className="ct-eyebrow mb-2">
                    {t('orders.section-instruments', { count: form.instruments?.length ?? 0 })}
                  </h3>
                  {(form.instruments || []).length > 0 ? (
                    <div className="rounded overflow-hidden mb-2" style={{ border: '1px solid var(--ct-border)' }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ background: 'var(--ct-bg)', borderBottom: '1px solid var(--ct-border)' }}>
                            <th className="text-left py-1.5 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('orders.instr-col-name')}</th>
                            <th className="text-left py-1.5 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('orders.instr-col-type')}</th>
                            <th className="text-left py-1.5 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('orders.instr-col-manufacturer')}</th>
                            <th className="text-left py-1.5 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('orders.instr-col-marking-1')}</th>
                            <th className="text-left py-1.5 px-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('orders.instr-col-marking-2')}</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(form.instruments || []).map((inst, idx) => (
                            <tr key={idx} className="ct-table-row">
                              <td className="py-1.5 px-2">{inst.name}</td>
                              <td className="py-1.5 px-2 font-mono text-xs">{inst.type}</td>
                              <td className="py-1.5 px-2">{inst.manufacturer}</td>
                              <td className="py-1.5 px-2 font-mono text-xs">{inst.marking1}</td>
                              <td className="py-1.5 px-2 font-mono text-xs">{inst.marking2}</td>
                              <td className="py-1.5 px-2">
                                <button
                                  data-testid={`remove-order-instrument-${idx}`}
                                  onClick={() => removeOrderInstrument(idx)}
                                  style={{ color: 'var(--ct-error)' }}
                                  title={t('orders.btn-remove-instrument-title')}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-3 px-3 rounded text-xs mb-2" style={{ background: 'var(--ct-bg)', border: '1px dashed var(--ct-border)', color: 'var(--ct-text-disabled)' }}>
                      {t('orders.instruments-empty')}
                    </div>
                  )}
                  {/* #15: výběr přístroje pouze z evidence (FK na instruments collection) */}
                  <div className="grid grid-cols-6 gap-2 items-center">
                    <select
                      data-testid="order-instrument-select"
                      value={newInstrumentId}
                      onChange={e => setNewInstrumentId(e.target.value)}
                      className="h-8 text-sm rounded px-2 col-span-3"
                      style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }}
                    >
                      <option value="">{t('orders.select-instrument-placeholder')}</option>
                      {instruments.map(i => (
                        <option key={i.id} value={i.id}>
                          {i.name || t('orders.instrument-no-name')} · {i.type || '—'} · {t('approvals.serial-prefix')} {i.serialNumber || '—'}
                        </option>
                      ))}
                    </select>
                    <Input
                      placeholder={t('orders.marking-1-placeholder')}
                      value={newMarking1}
                      onChange={e => setNewMarking1(e.target.value)}
                      className="h-8 text-sm"
                    />
                    <Input
                      placeholder={t('orders.marking-2-placeholder')}
                      value={newMarking2}
                      onChange={e => setNewMarking2(e.target.value)}
                      className="h-8 text-sm"
                    />
                    <Button
                      data-testid="add-order-instrument-btn"
                      onClick={addOrderInstrument}
                      disabled={!newInstrumentId}
                      size="sm"
                      className="ct-btn-primary h-8 gap-1"
                      title={!newInstrumentId ? t('orders.btn-add-instrument-disabled-title') : undefined}
                    >
                      <Plus className="w-3 h-3" /> {t('orders.btn-add-instrument')}
                    </Button>
                  </div>
                  <button
                    type="button"
                    data-testid="order-instrument-create-new"
                    onClick={() => setInstrumentDialogOpen(true)}
                    className="mt-2 flex items-center gap-1 text-xs hover:opacity-80"
                    style={{ color: 'var(--ct-accent)' }}
                  >
                    <Plus className="w-3 h-3" /> {t('orders.instrument-create-new')}
                  </button>
                  {instruments.length === 0 && (
                    <p className="text-xs mt-2" style={{ color: 'var(--ct-warning)' }}>
                      {t('orders.no-instruments-warn')}
                    </p>
                  )}
                </div>

                {/* UX vrstva A: sekce Související — viditelný tok zakázka → příjem → kalibrace */}
                {isExistingOrder && (intakeForOrder || calibrationsForOrder.length > 0) && (
                  <div>
                    <h3 className="ct-eyebrow mb-2">
                      {t('orders.section-related')}
                    </h3>
                    <div className="space-y-2">
                      {intakeForOrder ? (
                        <div className="p-2 rounded text-sm flex items-center justify-between gap-2"
                          style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)' }}>
                          <div className="min-w-0 flex items-center gap-2">
                            <RefLink type="intake" id={intakeForOrder.intakeProtocolNumber} />
                            <span className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                              {(intakeForOrder.instruments || []).length} přístrojů ·
                              {' '}{intakeForOrder.receivedAt ? new Date(intakeForOrder.receivedAt).toLocaleDateString(t('common.date-locale')) : ''}
                            </span>
                          </div>
                          <StatusBadge
                            tone={intakeForOrder.sentToCalibration ? 'success' : 'warning'}
                            className="flex-shrink-0"
                          >
                            {intakeForOrder.sentToCalibration ? t('orders.intake-status-sent') : t('orders.intake-status-ready')}
                          </StatusBadge>
                        </div>
                      ) : (
                        <div className="p-2 rounded text-xs italic" style={{ background: 'var(--ct-bg)', border: '1px dashed var(--ct-border)', color: 'var(--ct-text-disabled)' }}>
                          {t('orders.intake-not-created')}
                        </div>
                      )}
                      {calibrationsForOrder.length > 0 && (
                        <div className="rounded overflow-hidden" style={{ border: '1px solid var(--ct-border)' }}>
                          <div className="px-3 py-1.5 text-xs uppercase tracking-wider" style={{ background: 'var(--ct-bg)', color: 'var(--ct-text-secondary)' }}>
                            {t('orders.related-calibrations-title', { count: calibrationsForOrder.length })}
                          </div>
                          {calibrationsForOrder.map(c => (
                            <div key={c.id} className="px-3 py-1.5 text-sm flex items-center justify-between" style={{ borderTop: '1px solid var(--ct-border)' }}>
                              <span className="min-w-0 truncate">
                                {c.instrumentName || '—'}
                                <span className="text-xs ml-2" style={{ color: 'var(--ct-text-secondary)' }}>
                                  {c.laboratory} {c.methodologyCode ? `· ${c.methodologyCode}` : ''}
                                </span>
                              </span>
                              <StatusBadge status={c.status} className="flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="ct-form-field">
                  <label className="ct-form-label">{t('orders.label-notes')}</label>
                  <Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className="text-sm" rows={2} />
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <Button
                    data-testid="save-order-btn"
                    onClick={save}
                    disabled={!canSave}
                    className="ct-btn-primary gap-1"
                    title={!canSave ? validationErrors.join('; ') : undefined}
                  >
                    <Save className="w-4 h-4" /> {isDraft ? t('orders.btn-create') : t('orders.btn-save')}
                  </Button>
                  {isDraft && (
                    <Button onClick={cancelDraft} className="ct-btn-secondary">
                      {t('orders.btn-cancel')}
                    </Button>
                  )}
                  {/* Jedna primární akce podle stavu; alternativa je vizuálně podřízená. */}
                  {!isDraft && form.status === 'new' && (
                    <>
                      <Button data-testid="confirm-order-btn" onClick={confirm} className="ct-btn-primary gap-1">
                        <CheckCircle className="w-4 h-4" /> {t('orders.btn-confirm-order')}
                      </Button>
                      {(form.instruments || []).length > 0 && (
                        <Button
                          data-testid="receive-to-calibration-btn"
                          onClick={receiveToCalibration}
                          className="ct-btn-secondary gap-1"
                          title={t('orders.hint-receive-to-calibration')}
                        >
                          <PackageCheck className="w-4 h-4" /> {t('orders.btn-receive-to-calibration')}
                        </Button>
                      )}
                    </>
                  )}
                  {!isDraft && form.status === 'confirmed' && (
                    <>
                      {(form.instruments || []).length > 0 ? (
                        <>
                          <Button
                            data-testid="receive-to-calibration-btn"
                            onClick={receiveToCalibration}
                            className="ct-btn-primary gap-1"
                            title={t('orders.hint-receive-to-calibration')}
                          >
                            <PackageCheck className="w-4 h-4" /> {t('orders.btn-receive-to-calibration')}
                          </Button>
                          <Button data-testid="receive-order-btn" onClick={receive} className="ct-btn-secondary gap-1">
                            {t('orders.btn-receive-only')}
                          </Button>
                        </>
                      ) : (
                        <Button data-testid="receive-order-btn" onClick={receive} className="ct-btn-primary gap-1" style={{ color: 'var(--ct-success)' }}>
                          <PackageCheck className="w-4 h-4" /> {t('orders.btn-receive-order')}
                        </Button>
                      )}
                    </>
                  )}
                  {!canSave && (
                    <span className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                      {validationErrors.join('; ')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--ct-text-disabled)' }}>
            {t('orders.select-prompt')}
          </div>
        )}
      </div>

      <CompanyFormDialog
        open={companyDialogOpen}
        onOpenChange={setCompanyDialogOpen}
        initialName={companyDialogName}
        defaultIsCustomer
        onCreated={handleCompanyCreated}
      />
      <InstrumentFormDialog
        open={instrumentDialogOpen}
        onOpenChange={setInstrumentDialogOpen}
        onCreated={handleInstrumentCreated}
      />
    </div>
  );
}
